import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getCollectionReport, getCollectionReportWeeks, saveCollectionReport } from '../utils/api';

/* Weekly Collection Report — the office's paper sheet, filled in by hand.
   Monday–Friday, a row per payment: job name · product type · amount ·
   Deposit / 1/3 / Progress / Final · Cash / Finance / Credit Card ·
   date finance submitted · date finance hit the bank · comment (why it wasn't taken / was late).
   Day totals and the three summary boxes add themselves up.
     Finance Pending = finance rows with no "hit the bank" date yet
     In the Bank     = cash + credit card + finance rows that hit the bank
   Saved per week (Monday). Print / Save as PDF prints the sheet on letter paper. */

const DAYS = [['mon', 'MONDAY', 'Monday'], ['tue', 'TUESDAY', 'Tuesday'], ['wed', 'WEDNESDAY', 'Wednesday'], ['thu', 'THURSDAY', 'Thursday'], ['fri', 'FRIDAY', 'Friday']];
const ROWS = 6;                                   // rows each day starts with (like the paper sheet)
const STAGES = ['Deposit', '1/3', 'Progress', 'Final'];
const METHODS = ['Cash', 'Finance', 'Credit Card'];
const PRODUCTS = ['Roofing', 'Exterior Coating', 'Paint', 'Windows', 'Patio Doors', 'Gutters', 'Insulation', 'Cement', 'Fencing'];

const blankRow = () => ({ job: '', product: '', amount: '', stage: '', method: '', submitted: '', bank: '', comment: '' });
const blankDays = () => Object.fromEntries(DAYS.map(([k]) => [k, Array.from({ length: ROWS }, blankRow)]));
const num = v => { const x = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(x) ? x : 0; };
const money = v => (v ? '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const iso = d => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
const mondayOf = s => { const d = new Date(s + 'T12:00:00Z'); const wd = (d.getUTCDay() + 6) % 7; d.setUTCDate(d.getUTCDate() - wd); return iso(d); };
const todayCA = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
const mdY = s => (s ? `${+s.slice(5, 7)}/${+s.slice(8, 10)}/${s.slice(0, 4)}` : '');
const filled = r => Object.values(r).some(v => String(v || '').trim() !== '');

const CSS = `
/* ── On screen: full width, comfortable sizes ── */
.wcr-stage{padding:16px 20px 28px}
.wcr-bar{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:14px}
.wcr-bar .grow{flex:1}
.wcr-bar select,.wcr-bar input{border:1px solid #c9d4ce;border-radius:8px;padding:7px 10px;font:inherit;background:#fff}
.wcr-dirty{color:#8a5a00;font-weight:600;font-size:13px}
.wcr-scroll{overflow-x:auto}
.wcr-sheet{width:100%;min-width:1000px;background:#fff;border:1px solid #d9e2dc;border-radius:12px;overflow:hidden;font:14px/1.3 Inter,Helvetica,Arial,sans-serif;color:#1a1a1a;padding:0 0 20px}
.wcr-top{background:#1f4a30;color:#fff;display:flex;align-items:center;justify-content:space-between;padding:14px 24px;border-bottom:4px solid #c9a227}
.wcr-top h1{margin:0;font:700 24px Helvetica,Arial,sans-serif;letter-spacing:.01em;color:#fff}
.wcr-top small{display:block;color:#d9b54a;font-weight:700;font-size:11px;letter-spacing:.05em;margin-top:3px}
.wcr-week{display:flex;align-items:center;gap:10px;font-weight:700;font-size:14px}
.wcr-week span{display:inline-block;min-width:150px;height:32px;line-height:28px;background:#fff;color:#1a1a1a;border:2px solid #c9a227;padding:0 10px;font-weight:600}
.wcr-body{padding:16px 24px 0}
.wcr-t{width:100%;border-collapse:collapse;table-layout:fixed;font-size:14px}
.wcr-t col.c1{width:16%}.wcr-t col.c2{width:12.5%}.wcr-t col.c3{width:9.5%}.wcr-t col.c4{width:10%}.wcr-t col.c5{width:10%}.wcr-t col.c6{width:11%}.wcr-t col.c7{width:11%}.wcr-t col.c8{width:20%}
.wcr-sheet thead{background:none;color:inherit;position:static}
.wcr-t thead th{background:#e6ece7;color:#1a1a1a;border:1px solid #b9c6be;font-weight:700;font-size:12px;padding:8px 6px;text-align:center;text-transform:none;letter-spacing:0;border-radius:0}
.wcr-t td{border:1px solid #b9c6be;height:36px;padding:0;background:#fff}
.wcr-dayrow{display:flex;align-items:stretch;background:#1f4a30;color:#fff;border-left:6px solid #c9a227;margin-top:16px}
.wcr-dayrow b{flex:0 0 28.5%;padding:7px 10px;font-size:15px;letter-spacing:.02em}
.wcr-dayrow .tl{flex:0 0 0;padding:6px 8px;font-size:12px;align-self:center;text-align:right;white-space:nowrap;margin-left:auto}
.wcr-dayrow .tv{width:9.5%;background:#f8f0d8;color:#1a1a1a;font-weight:700;padding:7px 8px;text-align:right;border:1px solid #b9c6be}
.wcr-dayrow .dl{flex:1;text-align:right;padding:6px 8px;font-size:12px;align-self:center}
.wcr-dayrow .dv{width:11%;background:#fff;color:#1a1a1a;padding:7px 8px;border:1px solid #1f4a30}
.wcr-dayrow .cs{width:20%}
.wcr-in{width:100%;height:34px;border:0;background:transparent;font:inherit;padding:2px 8px;color:#1a1a1a;outline:none;border-radius:0;box-shadow:none;min-width:0}
.wcr-in:focus{background:#fffbe9;box-shadow:inset 0 0 0 2px #c9a227}
.wcr-in.r{text-align:right;padding-right:10px}
select.wcr-in{appearance:none;-webkit-appearance:none;cursor:pointer;padding-left:8px}
input[type=date].wcr-in{font-size:12.5px;padding:2px 4px}
.wcr-add{margin:6px 0 0;font-size:13px;padding:4px 10px;border:1px dashed #9fb3a7;background:#f6f9f7;border-radius:6px;cursor:pointer;color:#1f4a30}
.wcr-sum{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:22px;align-items:start;break-inside:avoid}
.wcr-pd{display:none;padding:1px 4px;font-size:8.5px}
.wcr-pc{display:none;padding:1px 4px;font-size:8px;line-height:1.2;white-space:pre-wrap;overflow-wrap:anywhere}
.wcr-box table{width:100%;border-collapse:collapse;font-size:14px}
.wcr-box thead th{background:#1f4a30;color:#fff;text-align:left;padding:7px 10px;font-size:12px;letter-spacing:.04em;text-transform:none;border-radius:0}
.wcr-box td{border:1px solid #b9c6be;padding:6px 10px;height:34px}
.wcr-box td.v{text-align:right;width:45%;font-weight:600}
.wcr-box tr.hl td{background:#f8f0d8;font-weight:700}
.wcr-foot{margin-top:12px;color:#6b6f6c;font-size:12px}

/* ── Printed: one letter page, same layout as the paper sheet ── */
@media print{
  @page{size:letter portrait;margin:0}
  body *:not(:has(.wcr-sheet)):not(.wcr-sheet):not(.wcr-sheet *){display:none!important}
  body *:has(.wcr-sheet){margin:0!important;padding:0!important;border:0!important;box-shadow:none!important;background:#fff!important;overflow:visible!important;position:static!important;transform:none!important;min-width:0!important}
  html,body{background:#fff!important;margin:0!important}
  .wcr-sheet,.wcr-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .wcr-sheet{width:8.5in;min-width:0;border:0;border-radius:0;font:9.5px/1.25 Helvetica,Arial,sans-serif;padding:0 0 .3in;break-inside:avoid}
  .wcr-top{padding:.12in .35in}
  .wcr-top h1{font-size:19px}
  .wcr-top small{font-size:8.5px;margin-top:2px}
  .wcr-week{font-size:11px;gap:8px}
  .wcr-week span{min-width:1.4in;height:.24in;line-height:.2in;padding:0 6px}
  .wcr-body{padding:.12in .35in 0}
  .wcr-t{font-size:9.5px}
  .wcr-t thead th{font-size:8.5px;padding:4px 3px}
  .wcr-t td{height:.18in}
  .wcr-dayrow{margin-top:.08in;border-left-width:5px}
  .wcr-dayrow b{padding:3px 6px;font-size:11px}
  .wcr-dayrow .tl,.wcr-dayrow .dl{padding:3px 6px;font-size:8.5px}
  .wcr-dayrow .tv,.wcr-dayrow .dv{padding:3px 5px}
  .wcr-in{height:.17in;padding:1px 4px}
  .wcr-in.r{padding-right:6px}
  select.wcr-in{padding-left:6px}
  .wcr-sum{gap:.12in;margin-top:.14in}
  .wcr-box table{font-size:9.5px}
  .wcr-box thead th{padding:3px 6px;font-size:8.5px}
  .wcr-box td{padding:2px 6px;height:.19in}
  .wcr-add,.wcr-noprint,input[type=date].wcr-in,.wcr-in.cm{display:none!important}
  .wcr-pd,.wcr-pc{display:block!important}
  .wcr-in:focus{background:transparent;box-shadow:none}
}
`;

export default function CollectionReport() {
  const [week, setWeek] = useState(() => mondayOf(todayCA()));
  const [days, setDays] = useState(blankDays);
  const [weeks, setWeeks] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const loadWeeks = useCallback(() => getCollectionReportWeeks().then(setWeeks).catch(() => setWeeks([])), []);
  useEffect(() => { loadWeeks(); }, [loadWeeks]);
  useEffect(() => {
    let live = true;
    getCollectionReport(week).then(r => {
      if (!live) return;
      const d = blankDays();
      for (const [k] of DAYS) {
        const saved = (r.days && r.days[k]) || [];
        d[k] = [...saved, ...Array.from({ length: Math.max(0, ROWS - saved.length) }, blankRow)];
      }
      setDays(d); setDirty(false); setInfo(r.saved ? { by: r.updated_by, at: r.updated_at } : null);
    }).catch(() => toast.error('Could not load that week'));
    return () => { live = false; };
  }, [week]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = (k, i, field, v) => { setDays(d => ({ ...d, [k]: d[k].map((r, j) => (j === i ? { ...r, [field]: v } : r)) })); setDirty(true); };
  const addRow = k => setDays(d => ({ ...d, [k]: [...d[k], blankRow()] }));
  const goWeek = w => { if (dirty && !window.confirm('You have changes that are not saved. Leave this week anyway?')) return; setWeek(mondayOf(w)); };

  async function save() {
    setBusy(true);
    try {
      const out = Object.fromEntries(DAYS.map(([k]) => [k, days[k].filter(filled)]));
      await saveCollectionReport(week, out);
      toast.success('Collection report saved'); setDirty(false); setInfo({ by: 'you', at: 'just now' }); loadWeeks();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); } finally { setBusy(false); }
  }

  const t = useMemo(() => {
    const all = DAYS.flatMap(([k]) => days[k]);
    const sum = f => all.filter(f).reduce((s, r) => s + num(r.amount), 0);
    const byDay = Object.fromEntries(DAYS.map(([k]) => [k, days[k].reduce((s, r) => s + num(r.amount), 0)]));
    return {
      byDay, week: Object.values(byDay).reduce((a, b) => a + b, 0),
      method: Object.fromEntries(METHODS.map(m => [m, sum(r => r.method === m)])),
      pending: sum(r => r.method === 'Finance' && !r.bank),
      bank: sum(r => r.method === 'Cash' || r.method === 'Credit Card' || (r.method === 'Finance' && !!r.bank)),
      stage: Object.fromEntries(STAGES.map(s => [s, sum(r => r.stage === s)])),
    };
  }, [days]);

  const weekOptions = useMemo(() => {
    const list = new Set(weeks.map(w => w.week_start)); list.add(week); list.add(mondayOf(todayCA()));
    return [...list].sort().reverse();
  }, [weeks, week]);
  const savedSet = new Set(weeks.map(w => w.week_start));

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header"><h2>Weekly Collection Report</h2></div>
      <div className="wcr-stage">
        <div className="wcr-bar wcr-noprint">
          <button type="button" className="btn btn-sm btn-outline" onClick={() => goWeek(addDays(week, -7))} aria-label="Previous week">‹</button>
          <select value={week} onChange={e => goWeek(e.target.value)} aria-label="Week">
            {weekOptions.map(w => <option key={w} value={w}>Week of {mdY(w)}{savedSet.has(w) ? ' · saved' : ''}</option>)}
          </select>
          <button type="button" className="btn btn-sm btn-outline" onClick={() => goWeek(addDays(week, 7))} aria-label="Next week">›</button>
          <input type="date" value={week} onChange={e => e.target.value && goWeek(e.target.value)} aria-label="Pick any day of the week" />
          <span className="grow">{dirty ? <span className="wcr-dirty">Not saved yet</span> : info ? <span style={{ color: '#6b6f6c', fontSize: 13 }}>Saved{info.by ? ` by ${info.by}` : ''}</span> : <span style={{ color: '#6b6f6c', fontSize: 13 }}>New sheet</span>}</span>
          <button type="button" className="btn btn-sm btn-outline" onClick={() => window.print()}>🖨 Print / Save as PDF</button>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>

        <div className="wcr-scroll"><div className="wcr-sheet">
          <div className="wcr-top"><div><h1>Weekly Collection Report</h1><small>CPR HOME IMPROVEMENT</small></div>
            <div className="wcr-week">Week of <span>{mdY(week)}</span></div></div>
          <div className="wcr-body">
            <table className="wcr-t" aria-hidden="true" style={{ marginBottom: 0 }}>
              <colgroup><col className="c1" /><col className="c2" /><col className="c3" /><col className="c4" /><col className="c5" /><col className="c6" /><col className="c7" /><col className="c8" /></colgroup>
              <thead><tr><th>Job Name</th><th>Product Type</th><th>Amount</th><th>Deposit / 1/3 /<br />Progress / Final</th><th>Cash / Finance /<br />Credit Card</th><th>Date Finance<br />Submitted</th><th>Date Finance<br />Hit the Bank</th><th>Comment<br />(why not taken / delayed)</th></tr></thead>
            </table>
            <datalist id="wcr-products">{PRODUCTS.map(p => <option key={p} value={p} />)}</datalist>
            {DAYS.map(([k, label, nice], di) => (
              <div key={k}>
                <div className="wcr-dayrow"><b>{label}</b><span className="tl">Total</span><span className="tv">{money(t.byDay[k])}</span><span className="dl">Date</span><span className="dv">{mdY(addDays(week, di))}</span><span className="cs" /></div>
                <table className="wcr-t">
                  <colgroup><col className="c1" /><col className="c2" /><col className="c3" /><col className="c4" /><col className="c5" /><col className="c6" /><col className="c7" /><col className="c8" /></colgroup>
                  <tbody>
                    {days[k].map((r, i) => (
                      <tr key={i}>
                        <td><input className="wcr-in" value={r.job} onChange={e => set(k, i, 'job', e.target.value)} aria-label={`${nice} row ${i + 1} job name`} /></td>
                        <td><input className="wcr-in" list="wcr-products" value={r.product} onChange={e => set(k, i, 'product', e.target.value)} aria-label={`${nice} row ${i + 1} product type`} /></td>
                        <td><input className="wcr-in r" inputMode="decimal" value={r.amount} onChange={e => set(k, i, 'amount', e.target.value)}
                          onBlur={e => { const n = num(e.target.value); if (String(e.target.value).trim()) set(k, i, 'amount', n ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''); }} aria-label={`${nice} row ${i + 1} amount`} /></td>
                        <td><select className="wcr-in" value={r.stage} onChange={e => set(k, i, 'stage', e.target.value)} aria-label={`${nice} row ${i + 1} stage`}><option value="" />{STAGES.map(s => <option key={s}>{s}</option>)}</select></td>
                        <td><select className="wcr-in" value={r.method} onChange={e => set(k, i, 'method', e.target.value)} aria-label={`${nice} row ${i + 1} payment type`}><option value="" />{METHODS.map(s => <option key={s}>{s}</option>)}</select></td>
                        <td><input type="date" className="wcr-in" value={r.submitted} onChange={e => set(k, i, 'submitted', e.target.value)} aria-label={`${nice} row ${i + 1} date finance submitted`} /><span className="wcr-pd">{mdY(r.submitted)}</span></td>
                        <td><input type="date" className="wcr-in" value={r.bank} onChange={e => set(k, i, 'bank', e.target.value)} aria-label={`${nice} row ${i + 1} date finance hit the bank`} /><span className="wcr-pd">{mdY(r.bank)}</span></td>
                        <td><input className="wcr-in cm" value={r.comment} maxLength={300} title={r.comment} onChange={e => set(k, i, 'comment', e.target.value)} aria-label={`${nice} row ${i + 1} comment: why the payment wasn't taken or was late`} /><span className="wcr-pc">{r.comment}</span></td>
                      </tr>))}
                  </tbody>
                </table>
                <button type="button" className="wcr-add" onClick={() => addRow(k)}>+ Add row to {nice}</button>
              </div>
            ))}

            <div className="wcr-sum">
              <div className="wcr-box"><table><thead><tr><th colSpan={2}>WEEK SUMMARY: BY DAY</th></tr></thead><tbody>
                {DAYS.map(([k, , nice]) => <tr key={k}><td>{nice}</td><td className="v">{money(t.byDay[k])}</td></tr>)}
                <tr className="hl"><td>Week Total</td><td className="v">{money(t.week)}</td></tr></tbody></table></div>
              <div className="wcr-box"><table><thead><tr><th colSpan={2}>BY PAYMENT TYPE</th></tr></thead><tbody>
                {METHODS.map(m => <tr key={m}><td>{m}</td><td className="v">{money(t.method[m])}</td></tr>)}
                <tr className="hl"><td>Finance Pending</td><td className="v">{money(t.pending)}</td></tr>
                <tr className="hl"><td>In the Bank</td><td className="v">{money(t.bank)}</td></tr></tbody></table></div>
              <div className="wcr-box"><table><thead><tr><th colSpan={2}>BY STAGE</th></tr></thead><tbody>
                {STAGES.map(s => <tr key={s}><td>{s}</td><td className="v">{money(t.stage[s])}</td></tr>)}</tbody></table></div>
            </div>
            <div className="wcr-foot wcr-noprint">Finance Pending = finance with no "hit the bank" date yet. In the Bank = cash + credit card + finance that hit the bank.</div>
          </div>
        </div></div>
      </div>
    </>
  );
}
